"use no memo";

/* eslint-disable complexity, consistent-return */
import * as Sentry from '@sentry/browser';

import { mParticle } from '@local/analytics';

import { useSelectedAddressStore } from 'store';

import { getRestaurantIdFromUrl } from 'helpers/url';
import { getFromChownowApi, postToChownowApi } from 'helpers/api';
import {
  setUserAuthStatus,
  checkDuplicateAddressesAgainstStorage,
} from 'helpers/customer';
import { siftPageView } from 'helpers/loggers';

export async function handleFetchUserProfile({
  setUser,
  sessionId,
  setIsLoaded,
}) {
  const { data, error } = await getFromChownowApi({
    endpoint: 'customer/me',
  });
  if (!error) {
    setUser(data);

    setUserAuthStatus(true);

    await checkDuplicateAddressesAgainstStorage(data.delivery?.addresses);

    // mParticle: check to see if mParticle already has the user email logged (see CN-20466)
    try {
      const currentUser = mParticle.Identity.getCurrentUser();
      const currentUserEmail =
        currentUser && currentUser.getUserIdentities().userIdentities.email;
      if (!currentUserEmail) {
        const identityRequest = {
          userIdentities: {
            email: data.email,
            customerid: data.id,
          },
        };
        // if no email is logged, send another identify request to log user info
        mParticle.Identity.identify(identityRequest);
      }
    } catch (err) {
      // fail silently when error occurs
      return;
    }

    // Sentry
    Sentry.setUser({ id: data.id });

    siftPageView(sessionId, data.id);
  } else {
    siftPageView(sessionId);
  }

  setIsLoaded(true);
}

export async function handleFetchUpdateUser(values, user, fetchUserValues) {
  // For user forms we are converting null to empty strings, curbside cannot be an empty string
  if (!values.curbside) {
    values.curbside = null; // eslint-disable-line no-param-reassign
  }

  const userMobileNumber = user.phone?.number;

  // if phone number updating, set mParticle user attribute
  if (userMobileNumber !== values.phone.number) {
    // mParticle
    try {
      const currentUser = mParticle.Identity.getCurrentUser();
      currentUser?.setUserAttribute('$Mobile', values.phone.number);
    } catch (err) {
      // fail silently when error occurs
    }
  }

  const { error } = await postToChownowApi({
    endpoint: `customer/${user.id}/contact-info`,
    body: values,
    method: 'PUT',
  });

  if (!error) {
    // mParticle IDSync — https://docs.mparticle.com/developers/sdk/web/idsync/#supported-identity-types
    const identityRequest = {
      userIdentities: {
        email: user.email,
        customerid: user.id,
      },
    };
    mParticle.Identity.login(identityRequest, (result) => {
      const mparticleUser = result.getUser();
      mparticleUser?.setUserAttribute('$Mobile', values.phone.number);
      mparticleUser?.setUserAttribute('user_is_optout', user.is_optout);
    });

    handleFetchUserProfile(fetchUserValues);
  } else {
    return error;
  }
}

export async function handleFetchLogout({
  setUser,
  setCardForm,
  setSelectedCardId,
  setSessionId,
  handleFetchRestaurant,
}) {
  // mParticle IDSync — pass an empty user identity object to clear session
  const identityRequest = {
    userIdentities: {},
  };
  mParticle.Identity.identify(identityRequest);

  const { error } = await postToChownowApi({
    endpoint: `customer/logout`,
  });

  if (!error) {
    setUserAuthStatus(false);

    // remove selected address when user logs out
    useSelectedAddressStore.getState().clearAddress();

    // Reset state on logout
    setUser({});
    setCardForm();
    setSelectedCardId(null);
    setSessionId('');

    const restaurantId = getRestaurantIdFromUrl();
    if (restaurantId) {
      handleFetchRestaurant(restaurantId);
    }
  }
}

export async function handleFetchRewards({
  setRewards,
  id,
  param,
  includeOptOuts,
}) {
  const inlcudeOptOutsParam = includeOptOuts ? '&include_opt_outs=true' : '';
  const companyOrRestaurantIdParam = param && id ? `?${param}=${id}` : '';
  const endpoint = `customer/me/rewards${companyOrRestaurantIdParam}${inlcudeOptOutsParam}`;
  const { data, error } = await getFromChownowApi({
    endpoint,
  });

  if (!error) {
    setRewards(data?.rewards);
  }
}
